import { getItemsConfig } from 'components/common/func';
import {
  ADD_TRANSACTION_AMOUNT_FIELDS,
  ADD_TRANSACTION_RECEIVER_GET_NAME,
  ADD_TRANSACTION_YOU_SEND_ANOTHER_NAME,
  ADD_TRANSACTION_YOU_SEND_NAME,
  AMOUNT_NUMBER_REGEX,
  DEBOUNCE_SEC,
  DESKTOP_MIN_WIDTH,
} from 'constants';
import CalculatorRoundingAnimationLottie from 'features/calculatorRounding/components/CalculatorRoundingAnimationLottie';
import FormInputRoundingMask from 'features/calculatorRounding/components/FormInputRoundingMask';
import { ROUNDING_DELAY } from 'features/calculatorRounding/const';
import useCalculatorRounding from 'features/calculatorRounding/hooks/useCalculatorRounding';
import useCalculatorRoundingStore from 'features/calculatorRounding/hooks/useCalculatorRoundingStore';
import { format } from 'helpers';
import { Fade } from 'helpers/animation';
import numberHelper from 'helpers/numberHelper';
import { useDiscountLoadingState } from 'hooks/discount-loading/DiscountLoadingContext';
import useFormAddTransactionStore from 'hooks/form-add-transaction/useFormAddTransactionStore';
import { SERVICE_ALERTS_TYPE } from 'hooks/service-alerts/model/ServiceAlertsModel';
import useCheckServiceAlerts from 'hooks/service-alerts/useCheckServiceAlerts';
import useServiceAlertsModal from 'hooks/service-alerts/useServiceAlertsModal';
import useServiceAlertsStore from 'hooks/service-alerts/useServiceAlertsStore';
import useServiceAlertsUtil from 'hooks/service-alerts/useServiceAlertsUtil';
import useDebounceCallback from 'hooks/useDebounceCallback';
import useLang from 'hooks/useLang';
import useScrollIntoView from 'hooks/useScrollIntoView';
import useStore from 'hooks/useStore';
import { delay } from 'lodash';
import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import Form from 'react-bootstrap/Form';
import { SET_ADD_TRANSACTION_INFORMATION } from 'store/action';
import styled from 'styled-components';
import { numberUtils, stringUtils } from 'utils';
import CustomCurrencyDropdown from '../CustomCurrencyDropdown/ver2';

// Submit-gating-only validation key for a range-level DISABLE. Intentionally
// not an amount field name: Step-2 submit gating reads Object.values(validation),
// while messages and the invalid border render from validation[field.name], so
// this blocks Continue without rendering anything.
const RANGE_OF_CURRENCY_CODE_DISABLE_NAME = 'rangeOfCurrencyCodeDisable';

const FormInputAmountValidation = ({
  field,
  countryConfig,
  receiveLimits,
  information,
  setInformation,
  validation,
  setValidation,
  currentRateValue,
  setCurrentRateValue,
  currentCurrencySelected,
  isDisabled,
  isFetching,
  setFetching,
  isChecking,
  setChecking,
  setOpenDiscountImportantPopup,
  onAmountChangeStart = () => {},
  isShowCalculatorForm,
  setShowCalculatorForm,
  ref,
}) => {
  const { t } = useLang();
  const { state, dispatch } = useStore();
  const { addTransactionInformation } = state;
  const { remittance, paymentMethod } = addTransactionInformation || {};
  const { currentRateStore, rate } = remittance || {};

  const [isPaste, setPaste] = useState(false);

  const dmCode = currentCurrencySelected?.dmCode;
  const countryCode = countryConfig?.countryCode;

  const isSendField = field.name === ADD_TRANSACTION_YOU_SEND_NAME;
  const isReceiverField = field.name === ADD_TRANSACTION_RECEIVER_GET_NAME;

  const { isAddAnotherReceivingCurrency } = information || {};

  // Witnesses a real amount input event for the service-alert observer, which
  // cannot see one by comparing the store value it samples. See
  // DiscountLoadingContext.
  const { notifyAmountInputEvent } = useDiscountLoadingState();

  const { fetchCurrentFee, fetchRate } = useCalculatorRounding();
  const {
    isRateFetching,
    isCalculatorRounding,
    setSendAmountRounding,
    resetCalculatorRounding,
    setRateFetching,
    roundingFieldName,
    setRoundingFieldName,
  } = useCalculatorRoundingStore();

  const {
    currentCurrencySelected: currentCurrencySelectedStore,
    currentRateAnotherStore,
    currentCurrencySelectedAnother: currentCurrencySelectedAnotherStore,
    isAddAnotherReceivingCurrency: isAddAnotherReceivingCurrencyStore,
  } = remittance || {};

  const { discountApplied } = paymentMethod || {};

  const { isServiceAlertsModalShowing } = useServiceAlertsModal();
  const { setReceivingAmount } = useFormAddTransactionStore();
  const { isPreventNextStep2 } = useServiceAlertsUtil();
  const { serviceAlertsByCountryCode } = useServiceAlertsStore();
  const {
    serviceAlertsByCurrencyCode1,
    serviceAlertsByCurrencyCode2,
    serviceAlertsByRangeOfCurrencyCode1,
  } = serviceAlertsByCountryCode || {};
  const { checkByCurrencyCode, checkByRangeOfCurrencyCode } =
    useCheckServiceAlerts();
  const { setValidationOfServiceAlertsByCurrencyCode } = checkByCurrencyCode();
  const { handleCheckRangeOfByCurrencyCodeValid } = checkByRangeOfCurrencyCode(
    'rangeOfCurrencyCode1',
  );
  const isCurrencyCodeSelectedDisable =
    serviceAlertsByCurrencyCode1?.type === SERVICE_ALERTS_TYPE.DISABLE;
  const [isCheckRangeOfByCurrencyCode, setCheckRangeOfByCurrencyCode] =
    useState(false);

  useEffect(() => {
    const receivingAmount = information[ADD_TRANSACTION_RECEIVER_GET_NAME];
    if (isReceiverField && receivingAmount) {
      setShowCalculatorForm(true);
    }
  }, [isReceiverField, isFetching]);

  useEffect(() => {
    const isCheckReceivingFieldsAfterNextClicked =
      isChecking && isPreventNextStep2;
    if (isCheckReceivingFieldsAfterNextClicked) {
      let newValidation = { ...validation };
      setValidationOfServiceAlertsByCurrencyCode({
        currencyCode: currentCurrencySelectedStore?.currencyCode,
        type: serviceAlertsByCurrencyCode1?.type,
        callback: (msg) => {
          newValidation = {
            ...newValidation,
            [ADD_TRANSACTION_RECEIVER_GET_NAME]: msg,
          };
        },
      });
      // if (isAddAnotherReceivingCurrency) {
      //   setValidationOfServiceAlertsByCurrencyCode({
      //     currencyCode: currentCurrencySelectedAnotherStore?.currencyCode,
      //     type: serviceAlertsByCurrencyCode2?.type,
      //     callback: (msg) => {
      //       newValidation = {
      //         ...newValidation,
      //         [ADD_TRANSACTION_RECEIVER_GET_ANOTHER_NAME]: msg,
      //       };
      //     },
      //   });
      // }
      setValidation(newValidation);
      setChecking(false);
    }
  }, [
    isReceiverField,
    isChecking,
    isPreventNextStep2,
    serviceAlertsByCurrencyCode1,
    serviceAlertsByCurrencyCode2,
    currentCurrencySelectedStore,
    currentCurrencySelectedAnotherStore,
    isAddAnotherReceivingCurrency,
  ]);

  /* checking rangeOfCurrencyCode when currencyCodeSelected is changed is no longer disabled */
  useEffect(() => {
    const receivingAmount = information[ADD_TRANSACTION_RECEIVER_GET_NAME];
    const isCheckRangeOfByCurrencyCodeValid =
      isCheckRangeOfByCurrencyCode &&
      receivingAmount &&
      !isCurrencyCodeSelectedDisable;
    if (isCheckRangeOfByCurrencyCodeValid) {
      const serviceAlertByRange = handleCheckRangeOfByCurrencyCodeValid({
        currencyCode: currentCurrencySelected?.currencyCode,
        receivingAmount,
      });

      // A range-level DISABLE must block Step-2 submit as soon as it is known.
      // isPreventNextStep2 reads the store slot, and the observer defers that
      // record until discounts settle -- roughly 800ms after the loading
      // overlay hides, and longer when no discount apply happens at all -- so
      // the submit guard is open for that whole window. The DISABLE modal is the
      // user-facing explanation, so this blocks silently.
      //
      // Deliberately keyed to a non-field name: submit gating reads
      // Object.values(validation), while the message and the invalid border
      // render from validation[field.name]. So this blocks submit and renders
      // nothing -- no message, no red border, no scroll-into-view. Keeping it
      // off the receiving-amount key also stops the receive/send limit pass
      // (which clears that key whenever the amount is within limits) from
      // silently unblocking submit on a later blur.
      const isRangeDisable =
        serviceAlertByRange?.type === SERVICE_ALERTS_TYPE.DISABLE;
      setValidation({
        ...validation,
        [RANGE_OF_CURRENCY_CODE_DISABLE_NAME]: isRangeDisable,
      });

      setCheckRangeOfByCurrencyCode(false);
    }
  }, [
    isCheckRangeOfByCurrencyCode,
    isCurrencyCodeSelectedDisable,
    currentCurrencySelected,
    information,
  ]);
  useEffect(() => {
    const isResetRemitanceForm =
      !isServiceAlertsModalShowing &&
      serviceAlertsByRangeOfCurrencyCode1?.type === SERVICE_ALERTS_TYPE.DISABLE;
    if (isResetRemitanceForm) {
      setInformation({
        ...information,
        [ADD_TRANSACTION_YOU_SEND_NAME]: '',
        [ADD_TRANSACTION_RECEIVER_GET_NAME]: '',
      });
    }
  }, [isServiceAlertsModalShowing, serviceAlertsByRangeOfCurrencyCode1]);

  const sendLimitConfig =
    countryConfig &&
    getItemsConfig(
      countryConfig?.sendLimits,
      countryConfig?.countryCode,
      null,
      true,
      null,
    );

  const receiveLimitConfig =
    countryConfig &&
    getItemsConfig(
      countryConfig?.receiveLimits,
      countryConfig?.countryCode,
      dmCode,
      null,
      currentCurrencySelected?.currencyCode,
    );

  const { maxSendLimit, minSendLimit } = countryConfig
    ? {
        maxSendLimit: Number(sendLimitConfig?.max || 30000),
        minSendLimit: Number(sendLimitConfig?.min || 20),
      }
    : {
        maxSendLimit: 30000,
        minSendLimit: 20,
      };

  const { maxReceiverLimit, minReceiverLimit } = countryConfig
    ? {
        maxReceiverLimit: Number(
          receiveLimitConfig?.maxAmount || maxSendLimit * currentRateValue,
        ),
        minReceiverLimit: Number(receiveLimitConfig?.minAmount || 0),
      }
    : {
        maxReceiverLimit: 7000000000,
        minReceiverLimit: 0,
      };

  const isCurrentCurrencySelectedChanged =
    currentCurrencySelected &&
    currentCurrencySelectedStore &&
    currentCurrencySelected?.currencyCode !==
      currentCurrencySelectedStore?.currencyCode;

  const validRef = useRef(null);
  useScrollIntoView(validRef, validation[field.name], [validation]);

  useEffect(() => {
    if (isCalculatorRounding || isRateFetching) return;

    if (
      isReceiverField &&
      countryConfig?.countryCode &&
      currentCurrencySelected &&
      (!currentRateStore ||
        (currentCurrencySelectedStore && isCurrentCurrencySelectedChanged))
    ) {
      if (isDisabled) {
        dispatch({
          type: SET_ADD_TRANSACTION_INFORMATION,
          payload: {
            ...addTransactionInformation,
            remittance: {
              ...remittance,
              currentRateStore: rate,
            },
          },
        });

        return;
      }

      fetchRate({
        countryCode: countryConfig?.countryCode,
        currency: currentCurrencySelected,
        setRate: setCurrentRateValue,
        rateProp: 'currentRateStore',
        currencyProp: 'currentCurrencySelected',
        information,
        setInformation,
      });

      delay(setRateFetching, ROUNDING_DELAY);
    }
  }, [
    countryConfig?.countryCode,
    isDisabled,
    currentRateStore,
    currentCurrencySelected,
    currentCurrencySelectedStore,
    isCurrentCurrencySelectedChanged,
  ]);

  // const isResetCurrencyAnother =
  //   !isAddAnotherReceivingCurrency &&
  //   !isAddAnotherReceivingCurrencyStore &&
  //   currentRateAnotherStore &&
  //   currentCurrencySelectedAnotherStore;
  // useEffect(() => {
  //   if (isResetCurrencyAnother) {
  //     const newInformation = {
  //       ...information,
  //       currentRateAnotherStore: null,
  //       currentCurrencySelectedAnother: null,
  //     };
  //     setInformation(newInformation);

  //     dispatch({
  //       type: SET_ADD_TRANSACTION_INFORMATION,
  //       payload: {
  //         ...addTransactionInformation,
  //         remittance: {
  //           ...addTransactionInformation.remittance,
  //           currentRateAnotherStore: null,
  //           currentCurrencySelectedAnother: null,
  //         },
  //       },
  //     });
  //   }
  // }, [isResetCurrencyAnother]);

  useEffect(() => {
    if (
      isSendField &&
      !isAddAnotherReceivingCurrency &&
      information[ADD_TRANSACTION_YOU_SEND_NAME]
    ) {
      let newValidation = { ...validation };

      newValidation = handleSendAmountValidation(
        information,
        newValidation,
        true,
      );

      setValidation(newValidation);
    }
  }, [isAddAnotherReceivingCurrency]);

  const handleSendAmountValidation = (information, validation, isDisable) => {
    let newValidation = { ...validation };

    const sendAmount = Number(information[ADD_TRANSACTION_YOU_SEND_NAME] || 0);
    const sendAnotherAmount = Number(
      information[ADD_TRANSACTION_YOU_SEND_ANOTHER_NAME] || 0,
    );
    const sendTotalAmount = numberUtils.mathRound(
      sendAmount + sendAnotherAmount,
    );

    if ((maxSendLimit && !minSendLimit) || (maxSendLimit && minSendLimit)) {
      if (sendTotalAmount > maxSendLimit) {
        newValidation = {
          ...newValidation,
          [ADD_TRANSACTION_YOU_SEND_NAME]: stringUtils.replaceKeyword(
            t(ADD_TRANSACTION_AMOUNT_FIELDS[0].msgMaxLimited),
            [
              {
                key: 'max',
                value: format.toAmountStr(maxSendLimit),
              },
            ],
          ),
          [ADD_TRANSACTION_YOU_SEND_ANOTHER_NAME]: stringUtils.replaceKeyword(
            t(ADD_TRANSACTION_AMOUNT_FIELDS[0].msgMaxLimited),
            [
              {
                key: 'max',
                value: format.toAmountStr(maxSendLimit),
              },
            ],
          ),
        };
      }
    }

    if ((!maxSendLimit && minSendLimit) || (maxSendLimit && minSendLimit)) {
      if (sendTotalAmount < minSendLimit) {
        newValidation = {
          ...newValidation,
          [ADD_TRANSACTION_YOU_SEND_NAME]: stringUtils.replaceKeyword(
            t(ADD_TRANSACTION_AMOUNT_FIELDS[0].msgMinLimited),
            [
              {
                key: 'min',
                value: format.toAmountStr(minSendLimit),
              },
            ],
          ),
          [ADD_TRANSACTION_YOU_SEND_ANOTHER_NAME]: stringUtils.replaceKeyword(
            t(ADD_TRANSACTION_AMOUNT_FIELDS[0].msgMinLimited),
            [
              {
                key: 'min',
                value: format.toAmountStr(minSendLimit),
              },
            ],
          ),
        };
      }
    }

    if (sendTotalAmount >= minSendLimit && sendTotalAmount <= maxSendLimit) {
      newValidation = {
        ...newValidation,
        [ADD_TRANSACTION_YOU_SEND_NAME]: '',
        [ADD_TRANSACTION_YOU_SEND_ANOTHER_NAME]: '',
      };
    }

    if (!isDisable) {
      newValidation = handleGetReceiverAmountValidation(
        information,
        newValidation,
        true,
      );
    }

    return newValidation;
  };

  const handleGetReceiverAmountValidation = (
    information,
    validation,
    isDisable = false,
  ) => {
    let newValidation = { ...validation };

    const getReceiverAmount = Number(
      information[ADD_TRANSACTION_RECEIVER_GET_NAME] || 0,
    );

    if (
      (maxReceiverLimit && !minReceiverLimit) ||
      (maxReceiverLimit && minReceiverLimit)
    ) {
      if (getReceiverAmount > maxReceiverLimit) {
        newValidation = {
          ...newValidation,
          [ADD_TRANSACTION_RECEIVER_GET_NAME]: stringUtils.replaceKeyword(
            t(ADD_TRANSACTION_AMOUNT_FIELDS[1].msgMaxLimited),
            [
              {
                key: 'max',
                value: format.toAmountStr(maxReceiverLimit),
              },
              {
                key: 'currency',
                value: currentCurrencySelected?.currencyCode,
              },
            ],
          ),
        };
      }
    }

    if (
      (!maxReceiverLimit && minReceiverLimit) ||
      (maxReceiverLimit && minReceiverLimit)
    ) {
      if (getReceiverAmount < minReceiverLimit) {
        newValidation = {
          ...newValidation,
          [ADD_TRANSACTION_RECEIVER_GET_NAME]: stringUtils.replaceKeyword(
            t(ADD_TRANSACTION_AMOUNT_FIELDS[1].msgMinLimited),
            [
              {
                key: 'min',
                value: format.toAmountStr(minReceiverLimit),
              },
              {
                key: 'currency',
                value: currentCurrencySelected?.currencyCode,
              },
            ],
          ),
        };
      }
    }

    if (
      getReceiverAmount >= minReceiverLimit &&
      getReceiverAmount <= maxReceiverLimit
    ) {
      newValidation = {
        ...newValidation,
        [ADD_TRANSACTION_RECEIVER_GET_NAME]: '',
      };
    }

    if (!isDisable) {
      newValidation = handleSendAmountValidation(
        information,
        newValidation,
        true,
      );
    }

    return newValidation;
  };

  useImperativeHandle(ref, () => ({
    handleGetReceiverAmountValidation,
  }));

  const handleSendAmountOnChange = (value, isChangeCurrency) => {
    if (isPaste && value > maxSendLimit) {
      value = Number(maxSendLimit);
    }

    let receiverValue = numberUtils.mathRound(
      numberUtils.mathRound(value) * currentRateValue,
    );

    if (
      (value > maxSendLimit && validation[field.name]) ||
      (receiverValue > maxReceiverLimit &&
        validation[ADD_TRANSACTION_RECEIVER_GET_NAME])
    ) {
      value = Number(value);

      receiverValue = numberUtils.mathRound(
        numberUtils.mathRound(value) * currentRateValue,
      );
    }

    if (currentCurrencySelected?.currencyCode === 'AUD') {
      receiverValue = Number(value);
    }

    if (currentCurrencySelected?.currencyCode === 'VND') {
      receiverValue = numberHelper.roundVndAmount(receiverValue);
    }

    if (
      currentCurrencySelected?.currencyCode !== 'VND' &&
      currentCurrencySelected?.currencyCode !== 'AUD'
    ) {
      receiverValue = numberUtils.mathRound(receiverValue);
    }

    if (isChangeCurrency) {
      receiverValue = numberUtils.mathRound(receiverValue);
    }

    document.getElementsByName(ADD_TRANSACTION_YOU_SEND_NAME)[0].value =
      format.toAmountCentStr(value);
    document.getElementsByName(ADD_TRANSACTION_RECEIVER_GET_NAME)[0].value =
      format.toAmountStr(receiverValue);

    value = numberUtils.mathRound(value);

    return [value, receiverValue];
  };

  const handleReceiverGetOnChange = (value) => {
    if (isPaste && value > maxReceiverLimit) {
      value = Number(maxSendLimit);
    }

    let sendValue = numberUtils.mathRound(
      numberUtils.mathRound(value) / currentRateValue,
    );

    if (
      (value > maxReceiverLimit && validation[field.name]) ||
      (sendValue > maxSendLimit && validation[ADD_TRANSACTION_YOU_SEND_NAME])
    ) {
      value = Number(value);

      sendValue = numberUtils.mathRound(
        numberUtils.mathRound(value) / currentRateValue,
      );
    }

    sendValue = numberUtils.mathRound(sendValue);

    document.getElementsByName(ADD_TRANSACTION_YOU_SEND_NAME)[0].value =
      format.toAmountStr(sendValue);
    document.getElementsByName(ADD_TRANSACTION_RECEIVER_GET_NAME)[0].value =
      format.toAmountCentStr(value);

    value = Number(value);

    if (currentCurrencySelected?.currencyCode !== 'VND') {
      value = numberUtils.mathRound(value);
    }

    if (currentCurrencySelected?.currencyCode === 'VND') {
      value = numberHelper.roundVndAmount(value);

      sendValue = numberUtils.mathRound(value / currentRateValue);
    }

    return [sendValue, value];
  };

  const { debounce: handleFetchCurrentFee } = useDebounceCallback(
    (information, setInformation, countryCode, dmCode) => {
      setSendAmountRounding(true);

      fetchCurrentFee(
        information,
        setInformation,
        countryCode,
        dmCode,
        maxSendLimit,
        () => {
          setFetching(false);

          const sendAmount = information[ADD_TRANSACTION_YOU_SEND_NAME] || 0;
          const receivingAmount =
            information[ADD_TRANSACTION_RECEIVER_GET_NAME] || 0;
          const currentCurrencySelected = information?.currentCurrencySelected;
          setReceivingAmount({
            [ADD_TRANSACTION_YOU_SEND_NAME]: sendAmount,
            [ADD_TRANSACTION_RECEIVER_GET_NAME]: receivingAmount,
            currentCurrencySelected,
          });

          // Paired with the write above: this is the only per-edit writer of
          // the amount the service-alert observer samples, and it is reached
          // only from the input's onChange handler. Announcing the event here
          // lets the observer tell a real amount change from a value that
          // merely round-tripped inside one debounce window.
          notifyAmountInputEvent(1);

          setCheckRangeOfByCurrencyCode(true);

          delay(resetCalculatorRounding, ROUNDING_DELAY + 100);
        },
        false,
        isReceiverField,
      );

      setPaste(false);
    },
    DEBOUNCE_SEC,
  );

  // Fix responsive
  // useEffect(() => {
  //   if (isEmpty(countryCode) || isEmpty(dmCode)) return;

  //   const sendAmount = information[ADD_TRANSACTION_YOU_SEND_NAME] || 0;
  //   const receivingAmount =
  //     information[ADD_TRANSACTION_RECEIVER_GET_NAME] || 0;
  //   const feeAmount = information?.feeAmount;
  //   if (sendAmount && receivingAmount && isEmpty(feeAmount)) {
  //     handleFetchCurrentFee(information, setInformation, countryCode, dmCode);
  //   }
  // }, [information, countryCode, dmCode]);

  const handleOnChange = (e) => {
    const { value, name } = e.target;

    let valueTrim = value ? format.toStrAmount(value.trim()) : '';
    let sendValue;
    let receiverValue;

    onAmountChangeStart();

    if (!isShowCalculatorForm) {
      setShowCalculatorForm(true);
    }

    if (isDisabled) {
      document.getElementsByName(name)[0].value = '';

      return;
    }

    if (valueTrim?.length <= 1 && !numberUtils.isNumber(valueTrim)) {
      document.getElementsByName(name)[0].value = '';

      return;
    }

    let newInformation = { ...information };
    let newValidation = { ...validation };

    if (!valueTrim) {
      newValidation = {
        ...newValidation,
        [ADD_TRANSACTION_YOU_SEND_NAME]: '',
        [ADD_TRANSACTION_RECEIVER_GET_NAME]: '',
      };
      setValidation(newValidation);

      document.getElementsByName(ADD_TRANSACTION_YOU_SEND_NAME)[0].value = '';
      document.getElementsByName(ADD_TRANSACTION_RECEIVER_GET_NAME)[0].value =
        '';

      newInformation = {
        ...newInformation,
        [ADD_TRANSACTION_YOU_SEND_NAME]: '',
        [ADD_TRANSACTION_RECEIVER_GET_NAME]: '',
        feeAmount: 0,
      };

      setFetching(true);
      setRoundingFieldName('');
      handleFetchCurrentFee(
        newInformation,
        setInformation,
        countryCode,
        dmCode,
      );

      return;
    }

    if (!numberUtils.isNumber(valueTrim)) {
      valueTrim = valueTrim.match(AMOUNT_NUMBER_REGEX)[0];

      document.getElementsByName(name)[0].value = valueTrim;
    }

    if (value.includes(' ')) {
      document.getElementsByName(name)[0].value = valueTrim;
    }

    if (isSendField) {
      [sendValue, receiverValue] = handleSendAmountOnChange(valueTrim);

      newInformation = {
        ...newInformation,
        [ADD_TRANSACTION_YOU_SEND_NAME]: sendValue,
        [ADD_TRANSACTION_RECEIVER_GET_NAME]: receiverValue,
      };
    }

    if (isReceiverField) {
      [sendValue, receiverValue] = handleReceiverGetOnChange(valueTrim);

      newInformation = {
        ...newInformation,
        [ADD_TRANSACTION_YOU_SEND_NAME]: sendValue,
        [ADD_TRANSACTION_RECEIVER_GET_NAME]: receiverValue,
      };
    }

    if (isSendField) {
      newValidation = handleSendAmountValidation(newInformation, newValidation);
    }

    if (isReceiverField) {
      newValidation = handleGetReceiverAmountValidation(
        newInformation,
        newValidation,
      );
    }

    setValidation(newValidation);

    setFetching(true);
    setRoundingFieldName(name);
    handleFetchCurrentFee(newInformation, setInformation, countryCode, dmCode);
  };

  const handleFormLabelOnClick = (e, name) => {
    e.preventDefault();
    const inputEle = document.getElementsByName(name)[0];

    if (inputEle) {
      inputEle.focus();
    }
  };

  const handleFormInputOnClick = () => {
    if (discountApplied && setOpenDiscountImportantPopup) {
      const inputEle = document.getElementsByName(field.name)[0];

      if (inputEle) {
        inputEle?.blur();
      }

      setOpenDiscountImportantPopup(true);
    }
  };

  const renderCurrencyText = () => {
    if (!isSendField) return null;
    return <CurrencyText $isDisabled={isDisabled}>AUD</CurrencyText>;
  };

  const renderCurrentRateValue = () => {
    if (!isReceiverField || !currentRateValue) return null;
    if (isRateFetching)
      return (
        <Rate>
          <CalculatorRoundingAnimationLottie mode={'dark'} />
        </Rate>
      );
    return (
      <Rate>{`(1 AUD = ${format.toAmountStr2(currentRateValue)} ${
        currentCurrencySelected?.currencyCode
      })`}</Rate>
    );
  };

  const renderCustomCurrencyDropdown = () => {
    if (!isReceiverField) return null;
    return (
      <CustomCurrencyDropdown
        sendName={ADD_TRANSACTION_YOU_SEND_NAME}
        receiverName={ADD_TRANSACTION_RECEIVER_GET_NAME}
        countryConfig={countryConfig}
        maxSendLimit={maxSendLimit}
        receiveLimits={receiveLimits}
        currentRateValue={currentRateValue}
        setCurrentRateValue={setCurrentRateValue}
        information={information}
        setInformation={setInformation}
        validation={validation}
        setValidation={setValidation}
        currentCurrencySelected={currentCurrencySelected}
        currencyProp="currentCurrencySelected"
        rateProp="currentRateStore"
        isDisabled={isDisabled}
        handleSendAmountOnChange={handleSendAmountOnChange}
        handleGetReceiverAmountValidation={handleGetReceiverAmountValidation}
        isFetching={isFetching}
        setFetching={setFetching}
        setOpenDiscountImportantPopup={setOpenDiscountImportantPopup}
      />
    );
  };

  const isForceShowRoundingMask =
    !!roundingFieldName &&
    roundingFieldName !== field?.name &&
    document.getElementsByName(field?.name)[0].value;
  const isCalculatorRoundingBySendAmount =
    isCalculatorRounding && !!information[ADD_TRANSACTION_YOU_SEND_NAME];
  const renderFormInputRoundingMask = () => {
    if (!isCalculatorRoundingBySendAmount && !isForceShowRoundingMask)
      return null;
    return (
      <FormInputRoundingMask
        mode={'dark'}
        isForceShowRoundingMask={isForceShowRoundingMask}
      />
    );
  };

  return (
    <FormGroupStyled ref={validRef}>
      <FormLabel onClick={(e) => handleFormLabelOnClick(e, field.name)}>
        {t(field.label)}
        {field.isRequired && <span>*</span>}
      </FormLabel>
      {renderFormInputRoundingMask()}
      <FormControl
        type={field.type}
        inputMode="decimal"
        autoComplete="off"
        name={field.name}
        defaultValue={
          information && information[field.name]
            ? format.toAmountCentStr(information[field.name])
            : ''
        }
        placeholder={field.placeholder}
        isInvalid={validation[field.name]}
        onChange={handleOnChange}
        onPaste={() => setPaste(true)}
        $isDisabled={isDisabled}
        readOnly={field.isReadOnly || isDisabled || isCalculatorRounding}
        onFocus={handleFormInputOnClick}
        $isRounding={
          isForceShowRoundingMask || isCalculatorRoundingBySendAmount
        }
      />
      {renderCurrencyText()}
      {renderCurrentRateValue()}
      {renderCustomCurrencyDropdown()}
      <FormControlFeedback hidden={false} type="invalid">
        {t(validation[field.name])}
      </FormControlFeedback>
    </FormGroupStyled>
  );
};

const FormGroupStyled = styled(Form.Group)`
  margin-bottom: 32px;
  position: relative;
  ${Fade};

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    margin-bottom: 34px;
  }
`;
const FormLabel = styled(Form.Label)`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  text-align: left;

  display: block;
  color: var(--ds-c-grey-dark);

  span {
    color: var(--c-required);
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;
const FormControl = styled(Form.Control)`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 20px;
  line-height: 25px;
  text-transform: uppercase;

  color: var(--ds-c-grey-dark);
  color: ${(props) => props.$isDisabled && 'var(--ds-c-grey-neutral)'};
  height: 44px;
  border: 1px solid var(--ds-c-grey-light);
  border: ${(props) => props.$isDisabled && 'none!important'};
  border-radius: 12px;
  padding-inline: 10px;
  background: var(--ds-c-white);

  &:focus {
    background: var(--ds-c-white) !important;
    border: 1px solid var(--ds-c-grey-dark) !important;
    border: ${(props) => props.$isDisabled && 'none!important'};
    color: ${(props) => props.$isDisabled && 'var(--ds-c-grey-neutral)'};
    outline: none !important;
    box-shadow: none !important;
  }

  &:invalid,
  &:invalid:focus,
  &.is-invalid {
    border: 1px solid var(--ds-c-red) !important;
    outline: none !important;
    box-shadow: none !important;
    background-image: none !important;
    padding-inline: 10px !important;
  }

  &[readonly] {
    background: ${(props) =>
      props.$isDisabled ? '#eef2f5!important' : 'var(--ds-c-white)!important'};
    cursor: not-allowed;
  }

  ${(props) =>
    props.$isRounding
      ? `
    color: transparent!important;
    `
      : ``}

  &::placeholder {
    font-family: var(--ff-primary);
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
    text-transform: none;
    color: var(--ds-c-blue-disabled) !important;

    ${(props) =>
      props.$isRounding
        ? `
    color: transparent!important;
    `
        : ``}
  }
`;
const FormControlFeedback = styled(Form.Control.Feedback)`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;

  color: var(--ds-c-red);
  position: absolute;
  margin-top: 4px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 14px;
    line-height: 17px;
  }
`;

const CurrencyText = styled.span`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  color: var(--ds-c-grey-dark);
  color: ${(props) => props.$isDisabled && 'var(--ds-c-grey-neutral)'};

  position: absolute;
  top: 40px;
  right: 10.5px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;
const Rate = styled.span`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  color: #1c3f88;

  position: absolute;
  top: 0px;
  right: 0px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

export default FormInputAmountValidation;
